/** Pure helpers for the SearchPanel — chip config, grouping, formatting,
 *  and match highlighting. No React, no I/O. */

export interface SearchResult {
  id: string;
  name: string;
  type: string;
  snippet: string;
  match_score: number;
  last_modified?: string | null;
  modified_by?: string | null;
}

/** Filter chips above the search input. `types: undefined` means "all". */
export interface FilterChip {
  id: string;
  label: string;
  types?: string[];
}

export const SEARCH_FILTER_CHIPS: FilterChip[] = [
  { id: "all", label: "All" },
  { id: "apex", label: "Apex", types: ["ApexClass", "ApexTrigger"] },
  { id: "lwc", label: "LWC", types: ["LightningComponentBundle", "AuraDefinitionBundle"] },
  { id: "objects", label: "Objects", types: ["CustomObject"] },
  { id: "fields", label: "Fields", types: ["CustomField"] },
  { id: "flows", label: "Flows", types: ["Flow", "FlowDefinitionView", "Workflow", "WorkflowRule"] },
  { id: "templates", label: "Templates", types: ["EmailTemplate"] },
  { id: "reports", label: "Reports", types: ["Report", "Dashboard"] },
];

/** Group results by `type` (preserves first-seen order). */
export const groupResultsByType = (
  results: SearchResult[],
): { type: string; items: SearchResult[] }[] => {
  const groups = new Map<string, SearchResult[]>();
  for (const r of results) {
    const arr = groups.get(r.type);
    if (arr) arr.push(r);
    else groups.set(r.type, [r]);
  }
  return Array.from(groups, ([type, items]) => ({ type, items }));
};

/** "2d ago", "3mo ago", etc. Empty string on missing/invalid input. */
export const formatRelativeTime = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const sec = Math.floor((Date.now() - t) / 1000);
  if (sec < 0) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
};

/** Split `text` into chunks marked as matching/not — caller wraps matches in <mark>. */
export interface HighlightChunk {
  text: string;
  match: boolean;
}

export const highlightTerm = (text: string, term: string): HighlightChunk[] => {
  if (!text || !term) return [{ text: text || "", match: false }];
  const lower = text.toLowerCase();
  const tlow = term.toLowerCase();
  const chunks: HighlightChunk[] = [];
  let cursor = 0;
  while (cursor < text.length) {
    const idx = lower.indexOf(tlow, cursor);
    if (idx < 0) {
      chunks.push({ text: text.slice(cursor), match: false });
      break;
    }
    if (idx > cursor) chunks.push({ text: text.slice(cursor, idx), match: false });
    chunks.push({ text: text.slice(idx, idx + term.length), match: true });
    cursor = idx + term.length;
  }
  return chunks.length ? chunks : [{ text, match: false }];
};

